/**
 * indentModalStore — tiny vanilla pub/sub store + a React hook subscribing to
 * it. We do not have zustand as a dep so we hand-roll a useSyncExternalStore
 * pattern (basically what zustand does under the hood, minus the bells).
 *
 * Mirrors the legacy Apps Script `viewIndentById(indentId)` global: any page
 * in the procurement realm can call `useIndentModalStore.getState().open(id)`
 * to pop the Indent Detail Modal (mounted once in ProcurementLayout).
 */

import { useSyncExternalStore } from 'react';

type State = { indentId: string | null };

let state: State = { indentId: null };
const listeners = new Set<() => void>();

function setState(next: State) {
  if (next.indentId === state.indentId) return;
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): State {
  return state;
}

export const useIndentModalStore = {
  /** Synchronous access to the current state — for `open`/`close` calls
   *  from event handlers and the like. */
  getState(): State & { open: (id: string) => void; close: () => void } {
    return {
      ...state,
      open: (id: string) => setState({ indentId: id }),
      close: () => setState({ indentId: null }),
    };
  },
  /** React hook — re-renders the subscriber when indentId changes. */
  use(): State & { open: (id: string) => void; close: () => void } {
    const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    return {
      ...snap,
      open: (id: string) => setState({ indentId: id }),
      close: () => setState({ indentId: null }),
    };
  },
};

/** Convenience helper for callers — `openIndent('IND-…')` is shorter than
 *  the full `useIndentModalStore.getState().open(...)` chain. */
export function openIndent(indentId: string): void {
  setState({ indentId });
}

export function closeIndent(): void {
  setState({ indentId: null });
}
